<script lang="ts">
export const elInputFileTypes = [
  'image',
  'document',
  'csv',
  'excel',
  'any',
  'zip',
  'patientsImportDataFpfV5',
  'patientsImportDataHippocrates',
  'patientsImportDataIatros',
  'patientsImportDataMdb2000',
  'patientsImportDataMillewin',
  'patientsImportDataPhronesis',
  'patientsImportDataFastamb',
  'patientsImportDataMedico2000',
  'patientsImportDataTServe',
  'patientsImportDataProfim',
  'patientsImportDataMillewinAttachments',
  'patientsImportDataAdvmednet',
] as const;
export type ElInputFileType = (typeof elInputFileTypes)[number];
</script>

<script setup lang="ts">
import { ref, computed, toRef, PropType } from 'vue';
import { useInput, withCommonElInputProps } from '@/forms/input';
import ElInputContainer from '@/forms/ElInputContainer.vue';
import ElFile from '@/ElFile.vue';
import ElIcon from '@/ElIcon.vue';
import { toast } from '@/ElToast.vue';

const props = defineProps({
  ...withCommonElInputProps(),
  modelValue: {
    type: Array as () => File[],
    required: true,
  },
  content: {
    type: String,
    default: undefined,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  fileType: {
    type: String as PropType<ElInputFileType>,
    default: 'document',
    required: false,
  },
});

const dragging = ref(false);
const inputField = ref<HTMLFormElement>();

const emit = defineEmits<{
  (event: 'update:modelValue', files: File[]): void;
}>();

const modelValue = toRef(props.modelValue);

const computedAcceptedFileExtensionsOrMimeType = computed<string[]>(() => {
  switch (props.fileType) {
    case 'patientsImportDataFpfV5':
    case 'patientsImportDataMdb2000':
    case 'patientsImportDataFastamb':
    case 'patientsImportDataProfim':
    case 'patientsImportDataHippocrates':
      return ['.mdb'];
    case 'patientsImportDataIatros':
      return ['.xml', '.mdb', '.sql', '.backup'];
    case 'patientsImportDataMillewin':
      return ['.sql', '.backup', '.db3'];
    case 'patientsImportDataPhronesis':
      return ['.sqlite', '.sqlite3', '.db', '.db3', '.s3db', '.sl3', '.gz'];
    case 'patientsImportDataTServe':
    case 'patientsImportDataMedico2000':
    case 'patientsImportDataMillewinAttachments':
    case 'patientsImportDataAdvmednet':
    case 'zip':
      return ['.zip'];
    case 'image':
      return ['image/jpeg', 'image/png'];
    case 'document':
      // any pdf or word-like document
      return [
        '.doc',
        '.docx',
        '.xml',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        '.pdf',
      ];
    case 'csv':
      return ['.csv'];
    case 'excel':
      return ['.xlsx'];
    default:
      return ['*'];
  }
});
const computedValidation = computed(() => {
  const validations = [props.validation];
  switch (props.fileType) {
    case 'image':
      validations.push('ext:jpg,png,jpeg');
      break;
    case 'document':
      validations.push('ext:doc,docs,xml,pdf');
      break;
    case 'csv':
      validations.push('ext:csv');
      break;
    case 'excel':
      validations.push('ext:xlsx');
      break;
    case 'patientsImportDataFpfV5':
    case 'patientsImportDataMdb2000':
    case 'patientsImportDataFastamb':
    case 'patientsImportDataProfim':
    case 'patientsImportDataHippocrates':
      validations.push('ext:mdb');
      break;
    case 'patientsImportDataIatros':
      validations.push('ext:xml,mdb,sql,backup');
      break;
    case 'patientsImportDataMillewin':
      validations.push('ext:sql,backup,.db3');
      break;
    case 'patientsImportDataPhronesis':
      validations.push('ext:sqlite,sqlite3,db,db3,s3db,sl3,gz');
      break;
    case 'patientsImportDataTServe':
    case 'patientsImportDataMedico2000':
    case 'patientsImportDataMillewinAttachments':
    case 'patientsImportDataAdvmednet':
    case 'zip':
      validations.push('ext:zip');
      break;
    default:
      break;
  }
  return validations;
});

const { value, errorMessage, uuid } = useInput(modelValue, computedValidation, props.name, (newValue: File[]) => {
  emit('update:modelValue', newValue);
});

const onChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (!target) return;
  loadFilesAndClearInput(target.files);
};

const onDrop = (event: DragEvent) => {
  if (!event.dataTransfer) return;
  loadFilesAndClearInput(event.dataTransfer.files);
  dragging.value = false;
};

const loadFilesAndClearInput = (newFiles: FileList | null) => {
  loadFiles(newFiles);
  if (inputField.value?.value) inputField.value.value = null;
};

const loadFiles = (newFiles: FileList | null) => {
  if (!newFiles) return;
  const newFilesArray = [...newFiles];

  if (
    !newFilesArray.every(file =>
      computedAcceptedFileExtensionsOrMimeType.value.some(
        extensionOrMimeType =>
          file.name.toLowerCase().endsWith(extensionOrMimeType) ||
          file.type.toLowerCase() === extensionOrMimeType ||
          extensionOrMimeType === '*',
      ),
    )
  ) {
    return toast('Formato non supportato, verifica di aver inserito un file/s con il formato corretto', { state: 'error' });
  }

  value.value = [...value.value, ...newFilesArray];
  if (!props.multiple && value.value.length > 1) {
    value.value = [value.value[0]];
  }
};

const removeFile = (idx: number) => {
  value.value = value.value.filter((_, i) => i !== idx);
};
</script>

<template>
  <ElInputContainer
    v-bind="$attrs"
    :name="uuid"
    :label="label"
    :error-message="props.errorMessage === false ? false : props.errorMessage || errorMessage"
    :hidden-error-message="hiddenErrorMessage"
    class="relative inline-block w-full"
  >
    <div class="flex flex-col gap-2 min-w-0 sm:min-w-80 min-h-20">
      <label
        v-if="value.length == 0 || multiple"
        tabIndex="0"
        :for="uuid"
        class="min-h-20 p-4 flex flex-col gap-2 items-center justify-center rounded border outline-none border-dashed w-full"
        :class="{
          'bg-white': !dragging,
          'bg-primary-light-hover': dragging,
          'hover:bg-primary-light-hover hover:text-primary-hover hover:border-primary-hover group-focus-within:bg-primary-light-focused group-focus-within:text-primary-focused group-focus-within:border-primary-light-focused':
            !disabled,
          'border-gray-300 text-gray-300 cursor-not-allowed': disabled,
          'border-primary': !disabled && !dragging,
          'border-primary-hover': !disabled && dragging,
        }"
        @dragover.prevent="dragging = true"
        @drop.prevent="onDrop"
        @dragenter.prevent="dragging = true"
        @dragleave.prevent="dragging = false"
      >
        <ElIcon
          name="ArrowUpCircleIcon"
          :class="{
            'w-5 h-5': content,
            'w-7 h-7': !content,
            'cursor-pointer': !disabled,
          }"
        ></ElIcon>
        <span v-if="content" class="text-xs">{{ content }}</span>
      </label>
      <input
        :id="uuid"
        ref="inputField"
        type="file"
        class="hidden"
        :accept="computedAcceptedFileExtensionsOrMimeType.join(',')"
        :disabled="disabled"
        :name="props.name || uuid"
        :multiple="multiple"
        @change.prevent="onChange"
      />
      <ElFile
        v-for="(file, index) in value"
        :key="`file-${index}`"
        variant="primary"
        :name="file.name"
        :format="file.type"
        class="w-full"
        :disabled="disabled"
        @close="removeFile(index)"
      />
    </div>
  </ElInputContainer>
</template>
